import { useState } from "react"
import { submitNewsletter } from "../services/newsletter-api"
import { phoneMask } from "@/utils/phoneMask"
import { Spinner } from "./spinner/Spinner"
import type { WidgetProps } from "../types/widget.props"
import { ZordUtil } from "@/utils/Zord"

export interface NewsletterForm {
    nome?: string
    email: string
    telefone?: string
}

interface FormProps extends WidgetProps {
    hasFieldTelefone?: boolean
    dinformname?: string
    className?: {
        form?: string
        nome?: string
        email?: string
        telefone?: string
        button?: string
    }
    id?: {
        nome?: string
        email?: string
        telefone?: string
        button?: string
    }
}

export function Form({
    configuracaoSite,
    textosPersonalizados,
    hasFieldTelefone = false,
    dinformname,
    className,
    id
}: Readonly<FormProps>) {

    const [form, setForm] = useState<NewsletterForm>({
        nome: "",
        email: "",
        telefone: ""
    })

    const [errors, setErrors] = useState<Partial<Record<keyof NewsletterForm, string>>>({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    function setField<K extends keyof NewsletterForm>(field: K, value: NewsletterForm[K]) {
        setForm(prev => ({ ...prev, [field]: value }))
        setErrors(prev => ({ ...prev, [field]: undefined }))
    }

    function validate(): boolean {
        const newErrors: typeof errors = {}

        if (configuracaoSite.newsletterExibeNome) {
            if (!form.nome || form.nome.trim().length < 3) {
                newErrors.nome = "Mínimo de 3 caracteres"
            }
        }

        if (!form.email) {
            newErrors.email = "E-mail é obrigatório"
        } else if (!Zord.Util.validator.emailValido(form.email)) {
            newErrors.email = "E-mail inválido"
        }

        if (hasFieldTelefone && configuracaoSite.newsletterExibeTelefone) {
            if (!form.telefone) {
                newErrors.telefone = "Telefone é obrigatório"
            } else if (!ZordUtil.contatoValido(form.telefone)) {
                newErrors.telefone = "Contato deve ter 10 ou 11 dígitos"
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault()

        if (!validate()) return

        try {
            setIsSubmitting(true)

            const payload = {
                nome: form.nome || undefined,
                email: form.email,
                telefone: form.telefone?.replace(/\D/g, "") || undefined
            }

            await submitNewsletter(payload, configuracaoSite, hasFieldTelefone)

            Zord.analytics.registerEvent("user_register", {
                location: "Landingpage Newsletter",
                consent: true,
                data: {
                    email: form.email,
                    name: form.nome,
                    phone: form.telefone,
                }
            })

            Zord.showToast({
                title: 'Cadastro',
                icon: 'success',
                text: 'Cadastro efetuado com sucesso!',
                timer: 2000
            })

            setForm({ nome: "", email: "", telefone: "" })
            setErrors({})

        } catch (err) {
            Zord.erro((err as Error).message || "Erro inesperado.")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            {isSubmitting && <Spinner />}

            <form className={className?.form} onSubmit={onSubmit} {...{ dinformname }}>

                {configuracaoSite.newsletterExibeNome && (
                    <div className="field nome-input">
                        <input
                            type="text"
                            name="nome"
                            placeholder="Digite o seu nome..."
                            maxLength={100}
                            id={id?.nome}
                            className={className?.nome}
                            value={form.nome}
                            onChange={e => setField("nome", e.target.value)}
                        />
                        {errors.nome && <span className="error-message">{errors.nome}</span>}
                    </div>
                )}

                <div className="field email-input">
                    <input
                        type="email"
                        name="email"
                        placeholder="Digite o seu e-mail..."
                        maxLength={100}
                        id={id?.email}
                        className={className?.email}
                        value={form.email}
                        onChange={e => setField("email", e.target.value)}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                {(hasFieldTelefone && configuracaoSite.newsletterExibeTelefone) && (
                    <div className="field telefone-input">
                        <input
                            type="text"
                            name="telefone"
                            placeholder="Digite o seu telefone..."
                            maxLength={15}
                            id={id?.telefone}
                            className={className?.telefone}
                            value={form.telefone}
                            onChange={e => setField("telefone", phoneMask(e.target.value))}
                        />
                        {errors.telefone && (
                            <span className="error-message">{errors.telefone}</span>
                        )}
                    </div>
                )}

                <button
                    id={id?.button}
                    className={className?.button}
                    type="submit"
                    disabled={isSubmitting}
                >
                    {textosPersonalizados.newsletterButton}
                </button>
            </form>
        </>
    )
}
